import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { HeatPicker } from "@/components/HeatPicker";
import { FLAME } from "@shared/heat";

interface HeatPromptItem {
  id: string;
  name: string;
  imageUrl: string | null;
}

// One-time prompt for sellers who listed hot sauces before heat ratings existed.
// It pops on login, collects a heat level for each unrated sauce inline, and
// dismisses for good on submit — no hunting through the edit form.
export function HeatPromptGate() {
  const { user, isAuthenticated } = useAuth();
  const canSell = !!(user as any)?.canSell;

  const { data } = useQuery<{ items: HeatPromptItem[] }>({
    queryKey: ["/api/seller/heat-prompt"],
    queryFn: async () => {
      const res = await fetch("/api/seller/heat-prompt", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: isAuthenticated && canSell,
  });

  const items = data?.items ?? [];
  const [levels, setLevels] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!isAuthenticated || !canSell || items.length === 0 || dismissed) return null;

  const allRated = items.every((it) => levels[it.id] != null);

  const finish = async (save: boolean) => {
    setSubmitting(true);
    try {
      await apiRequest("POST", "/api/seller/heat-prompt", { levels: save ? levels : {} });
      await queryClient.invalidateQueries({ queryKey: ["/api/seller/heat-prompt"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      setDismissed(true);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-2xl bg-background shadow-xl">
        <div className="p-6 pb-3">
          <h2 className="text-xl font-bold text-foreground">Set the heat {FLAME}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Rate how hot each of your sauces is so buyers know what they're in for. A few taps and
            you're done.
          </p>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6">
          {items.map((it) => (
            <div key={it.id}>
              <div className="mb-2 flex items-center gap-3">
                {it.imageUrl && (
                  <img src={it.imageUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
                )}
                <span className="font-semibold text-foreground">{it.name}</span>
              </div>
              <HeatPicker
                value={levels[it.id] ?? null}
                onChange={(level) => setLevels((prev) => ({ ...prev, [it.id]: level }))}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 p-6 pt-4">
          <Button onClick={() => finish(true)} disabled={!allRated || submitting}>
            {submitting ? "Saving…" : allRated ? "Save heat levels" : "Rate every sauce to save"}
          </Button>
          <button
            type="button"
            onClick={() => finish(false)}
            disabled={submitting}
            className="text-xs text-muted-foreground underline"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
